// 損益ページ（経営概況 / 営業）で共有する定数・型・表示ヘルパー
// 経営ページと営業ページは表示ブロックを共通化しており、その土台となる定義をここに集約する。

// 🔍 現在の決算年度を算出 (9月開始・8月決算)
export const calculateCurrentFiscalYear = (): number => {
  const now = new Date();
  const month = now.getMonth() + 1; // 1-indexed
  const year = now.getFullYear();
  // 9月以降なら、次の年が決算年度(FY)になる
  return month >= 9 ? year + 1 : year;
};

// 表示する月の順序 (9月から開始)
export const MONTH_ORDER = [9, 10, 11, 12, 1, 2, 3, 4, 5, 6, 7, 8];

// 業績範囲 (単月 / 四半期 / 半期 / 通期) ごとの対象月。会計年度は9月開始のため 1Q=9〜11月, 4Q=6〜8月
// 'month' (単月) の対象月は表示中の月に依存するため静的には持たず、getTargetMonths で動的に算出する
export type PerformanceRange = 'month' | 'annual' | '1Q' | '2Q' | 'H1' | '3Q' | '4Q' | 'H2';
export const PERFORMANCE_RANGE_MONTHS: Record<PerformanceRange, number[]> = {
  month: [], // 単月は currentMonth に依存するため getTargetMonths で差し替える (この値は未使用)
  annual: MONTH_ORDER,
  '1Q': [9, 10, 11],
  '2Q': [12, 1, 2],
  H1: [9, 10, 11, 12, 1, 2],
  '3Q': [3, 4, 5],
  '4Q': [6, 7, 8],
  H2: [3, 4, 5, 6, 7, 8],
};
// 業績範囲のラジオ選択肢 (表示順)。先頭の単月のみ、隣接する月セレクトで対象月を指定する
export const PERFORMANCE_RANGE_OPTIONS: { value: PerformanceRange; label: string }[] = [
  { value: 'month', label: '単月' },
  { value: '1Q', label: '1Q' },
  { value: '2Q', label: '2Q' },
  { value: 'H1', label: '上期' },
  { value: '3Q', label: '3Q' },
  { value: '4Q', label: '4Q' },
  { value: 'H2', label: '下期' },
  { value: 'annual', label: '通期' },
];

// 業績範囲に対応する集計対象月を返す。単月 (month) のときは選択中の月のみを対象とする
export const getTargetMonths = (range: PerformanceRange, currentMonth: number): number[] =>
  range === 'month' ? [currentMonth] : PERFORMANCE_RANGE_MONTHS[range];

// 期(決算年度)の選択肢に関する定数・関数
// 2024年度以前は OBC データが存在しないため 2025年度 (31期) を下限とする
// ラベルは「YYYY年度（NN期）」表記 (期 = FY - 1994、2026年度 = 32期 基準)
export const FISCAL_YEAR_MIN = 2025;
export const fiscalYearToPeriod = (fy: number): number => fy - 1994;
export const generateFiscalYearOptions = (): { value: number; label: string }[] => {
  const currentFY = calculateCurrentFiscalYear();
  const startFY = Math.max(FISCAL_YEAR_MIN, currentFY - 2);
  const endFY = Math.max(currentFY + 1, FISCAL_YEAR_MIN);
  const options: { value: number; label: string }[] = [];
  for (let fy = startFY; fy <= endFY; fy++) {
    options.push({ value: fy, label: `${fy}年度（${fiscalYearToPeriod(fy)}期）` });
  }
  return options;
};

export type BudgetVersion = {
  version_code: string;
  version_name: string;
  sort_order: number;
};

// 部署グループ別 売上高の1行 (純売上高を actual/budget/prev で保持)
// 月次内訳 (sales_*_by_month) を持ち、業績範囲 (通期/半期/四半期) の合算はフロントで行う
// 各種比率はフロント側で算出する。営業利益は仕訳起票ルール未徹底のため対象外
export type DepartmentPlRow = {
  department_group: string;  // '1000'〜'6000' / 'OTHER'
  department_name: string;
  sales_actual: number;
  sales_budget: number;
  sales_prev: number;
  // 会計月 1〜12 をキーにした月次内訳 (業績範囲切替用)
  sales_actual_by_month?: Record<string, number>;
  sales_budget_by_month?: Record<string, number>;
  sales_prev_by_month?: Record<string, number>;
  // 工事売上高 (4101D) の実績。通期合計 + 月次内訳 (カード下段の工事高表示用)
  koji_actual?: number;
  koji_actual_by_month?: Record<string, number>;
};

// 拠点別 売上高の1行。売上の3系列・月次内訳は DepartmentPlRow と同じ構成 (部署別 API の branch_data で返る)
export type BranchPlRow = Omit<DepartmentPlRow, 'department_group' | 'department_name'> & {
  branch_code: string;  // mst_branch.branch_code / 'OTHER' (共通部門)
  branch_name: string;
};

// 従業員 月次在籍数推移の1行 (支店×部門)。month_{9..8}_headcount / _hires / _leaves / _headcount_prev を持つ
export type StaffHeadcountRow = {
  branch: string;
  branch_name: string;
  department: string;
  department_name: string;
  // include_employment_status=true で取得した場合のみ付与 (社員/契約/パート/派遣/役員/外注/出向/未設定)
  employment_status?: string;
  [key: string]: any;
};

// 試算表マトリクスの1行 (OBC 実績 + 予算)。月次 month_{1..12}_actual/_budget/_actual_prev を持つ
export type TrialBalanceRow = {
  account_code?: string;
  account_name?: string;
  sub_account_code?: string;
  sub_account_name?: string;
  category_l2_name?: string;
  [key: string]: any;
};

// 百万円単位 + 3桁カンマ整形 (例 1234.5 -> 1,234.5)
export const fmtMillion = (v: number): string =>
  (v / 1000000).toLocaleString('ja-JP', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
